// Multi-Agent Policy Audit detail view — Slice 19, DESIGN §5.4.
//
// Read-only HR Admin / HR Viewer view. Renders:
//   - Status header (audit lifecycle pill + processedAt timestamp).
//   - Supervisor's synthesised report (executive summary + prioritised
//     actions) — the primary HR-facing content.
//   - Collapsed per-agent raw findings (Compliance + Empathy) for
//     operators who want to see what each agent flagged before the
//     Supervisor's synthesis.
//
// The audit pipeline never auto-polls here — the list page on /hr/policies
// handles in-flight polling. By the time HR navigates to the detail view,
// the audit is either terminal (Ready / Failed / Unavailable) or still
// Pending/Processing in which case we show the in-flight copy without
// background polling (a refresh shows the latest).

import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  AuditFindingSeverity,
  ComplianceAgentOutput,
  ComplianceFinding,
  EmpathyAgentOutput,
  EmpathyFinding,
  PolicyAuditReport,
  PolicyAuditStatus,
  SynthesisedReport,
} from '@besideu/shared/policies';
import { api, ApiError } from '@/lib/api.ts';
import { Card } from '@/components/ui/Card.tsx';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';

export function HrPolicyAudit() {
  const { id = '' } = useParams<{ id: string }>();
  const audit = useQuery({
    queryKey: ['policy-audit', id],
    queryFn: () => api.get<{ audit: PolicyAuditReport }>(`/api/hr/policies/${id}/audit`),
    enabled: id !== '',
  });

  if (audit.isPending) return <p className="text-muted-foreground">Loading audit…</p>;
  if (audit.error) {
    if (audit.error instanceof ApiError && audit.error.status === 404) {
      return <NotFound />;
    }
    return <p className="text-traffic-red">Could not load this audit.</p>;
  }

  const report = audit.data.audit;
  return (
    <section className="space-y-6">
      <Link
        to="/hr/policies"
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to policies
      </Link>

      {report.status === 'Ready' && report.synthesised ? (
        <AuditHero report={report.synthesised} processedAt={report.processedAt} />
      ) : (
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Policy audit</h1>
          <p className="text-sm text-muted-foreground">
            Multi-agent review of compliance against UK statutory bereavement law and tone / empathy
            of the language used. Everything Maeve says is advisory; HR Admins remain the
            decision-makers on policy wording.
          </p>
          <StatusBanner
            status={report.status}
            lastError={report.lastError}
            processedAt={report.processedAt}
          />
        </header>
      )}

      {report.status === 'Ready' && report.synthesised ? (
        <SynthesisedReportPanel report={report.synthesised} />
      ) : null}

      {report.compliance ? (
        <AgentFindingsPanel
          title="Compliance findings"
          subtitle="UK Employment Rights Act 2025 / ACAS conformance"
          output={report.compliance}
        />
      ) : null}

      {report.empathy ? (
        <AgentFindingsPanel
          title="Empathy &amp; tone findings"
          subtitle="Language warmth and clarity for a bereaved employee"
          output={report.empathy}
        />
      ) : null}
    </section>
  );
}

// Ready-state dark hero (User Journeys v2 — the "AI Policy Audit" banner:
// scores + a Maeve summary line, Maeve's face floating right).
function AuditHero({
  report,
  processedAt,
}: {
  report: SynthesisedReport;
  processedAt: string | null;
}) {
  const actionCount = report.prioritisedActions.length;
  return (
    <section className="relative overflow-hidden rounded-2xl bg-ink px-6 py-7 text-white shadow-dark-section sm:px-8">
      <div className="relative z-10 max-w-2xl space-y-4">
        <p className="text-[0.68rem] font-semibold tracking-[0.18em] text-primary uppercase">
          AI Policy Audit
        </p>
        <div className="flex flex-wrap gap-8">
          <ScoreStat label="Compliance" value={report.overallComplianceScore} />
          <ScoreStat label="Empathy &amp; tone" value={report.overallEmpathyScore} />
        </div>
        <p className="text-sm text-white/70">
          Maeve reviewed this policy against UK statutory bereavement law and the warmth of its
          language.{' '}
          {actionCount > 0
            ? `${actionCount} prioritised ${actionCount === 1 ? 'action is' : 'actions are'} listed below.`
            : 'No prioritised actions — see the detail below.'}{' '}
          Advisory only — review with legal counsel before publishing changes.
          {processedAt ? (
            <>
              {' '}
              Generated{' '}
              <time dateTime={processedAt}>{new Date(processedAt).toLocaleString('en-GB')}</time>.
            </>
          ) : null}
        </p>
      </div>
      <div className="pointer-events-none absolute top-1/2 right-4 hidden -translate-y-1/2 sm:right-8 sm:block">
        <MaeveAvatar size={92} />
      </div>
    </section>
  );
}

function ScoreStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="font-heading text-3xl font-bold tabular-nums">
        {value}
        <span className="text-lg font-semibold text-white/50">/100</span>
      </p>
      <p className="mt-0.5 text-xs tracking-wide text-white/60 uppercase">{label}</p>
    </div>
  );
}

function StatusBanner({
  status,
  lastError,
  processedAt,
}: {
  status: PolicyAuditStatus;
  lastError: string | null;
  processedAt: string | null;
}) {
  if (status === 'Pending' || status === 'Processing') {
    return (
      <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        The audit is still running. Refresh in a moment to see the report.
      </p>
    );
  }
  if (status === 'Unavailable') {
    return (
      <p className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 px-3 py-2 text-sm text-traffic-amber-text">
        Policy auditing isn't available yet on this organisation. A besideU adviser needs to sign
        off the audit prompts before reports can be generated.
      </p>
    );
  }
  if (status === 'Failed') {
    return (
      <p className="rounded-md border border-traffic-red/40 bg-traffic-red/10 px-3 py-2 text-sm text-traffic-red">
        The audit didn't finish.{' '}
        {lastError ? <span className="font-mono text-xs">{lastError}</span> : null}
      </p>
    );
  }
  // Ready
  return (
    <p className="text-sm text-muted-foreground">
      Report generated{' '}
      {processedAt ? (
        <time dateTime={processedAt}>{new Date(processedAt).toLocaleString('en-GB')}</time>
      ) : (
        'recently'
      )}
      . Advisory only — review with your legal counsel before publishing changes.
    </p>
  );
}

function SynthesisedReportPanel({ report }: { report: SynthesisedReport }) {
  return (
    <Card className="space-y-4">
      <h2 className="text-lg font-semibold">Summary &amp; recommended actions</h2>
      <p className="text-sm whitespace-pre-wrap">{report.executiveSummary}</p>
      {report.prioritisedActions.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Prioritised actions</h3>
          <ul className="space-y-2">
            {report.prioritisedActions.map((a, i) => (
              <li key={i} className="rounded-md border border-border p-3 text-sm">
                <div className="flex flex-wrap items-baseline gap-2">
                  <PriorityPill priority={a.priority} />
                  <span className="font-medium">{a.action}</span>
                  <span className="text-xs text-muted-foreground">
                    ({sourceLabel(a.sourceAgent)})
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">{a.rationale}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

function AgentFindingsPanel({
  title,
  subtitle,
  output,
}: {
  title: string;
  subtitle: string;
  output: ComplianceAgentOutput | EmpathyAgentOutput;
}) {
  return (
    <details className="rounded-lg border border-border p-6">
      <summary className="cursor-pointer text-base font-medium">
        {title}{' '}
        <span className="font-normal text-muted-foreground">
          — overall {output.overallScore}/100
        </span>
      </summary>
      <div className="mt-4 space-y-3 text-sm">
        <p className="text-muted-foreground">{subtitle}</p>
        <p className="whitespace-pre-wrap">{output.summary}</p>
        {output.findings.length > 0 ? (
          <ul className="space-y-2">
            {output.findings.map((f, i) => (
              <FindingItem key={i} finding={f} />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No specific findings.</p>
        )}
      </div>
    </details>
  );
}

function FindingItem({ finding }: { finding: ComplianceFinding | EmpathyFinding }) {
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <SeverityPill severity={finding.severity} />
        <span className="font-medium">{finding.title}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap">{finding.detail}</p>
      {finding.policyExcerpt ? (
        <blockquote className="mt-2 border-l-2 border-border pl-3 text-xs text-muted-foreground italic">
          {finding.policyExcerpt}
        </blockquote>
      ) : null}
      {finding.suggestedEdit ? (
        <p className="mt-2 text-xs">
          <span className="font-medium">Suggested edit:</span> {finding.suggestedEdit}
        </p>
      ) : null}
    </li>
  );
}

function SeverityPill({ severity }: { severity: AuditFindingSeverity }) {
  const cls =
    severity === 'high'
      ? 'bg-traffic-red/20 text-traffic-red'
      : severity === 'medium'
        ? 'bg-traffic-amber/20 text-traffic-amber-text'
        : severity === 'low'
          ? 'bg-muted text-muted-foreground'
          : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}>
      {severity}
    </span>
  );
}

function PriorityPill({
  priority,
}: {
  priority: SynthesisedReport['prioritisedActions'][number]['priority'];
}) {
  const cls =
    priority === 'critical'
      ? 'bg-traffic-red/20 text-traffic-red'
      : priority === 'high'
        ? 'bg-traffic-amber/20 text-traffic-amber-text'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}>
      {priority}
    </span>
  );
}

function sourceLabel(s: 'compliance' | 'empathy' | 'both'): string {
  if (s === 'both') return 'compliance + empathy';
  return s;
}

function NotFound() {
  return (
    <section className="space-y-3">
      <h1 className="text-2xl font-semibold">Audit not found</h1>
      <p className="text-muted-foreground">
        Either this policy doesn't have an audit yet, or it doesn't exist in your organisation.
      </p>
      <Link to="/hr/policies" className="text-sm text-primary underline-offset-4 hover:underline">
        ← Back to policies
      </Link>
    </section>
  );
}
